import React, { useRef } from "react";
import {
  CheckCircleIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { getBusinessSubTypeLabel } from "@/utils";

const tiers = ["SILVER", "GOLD", "PLATINUM"];
const PLAN_FEATURES = [
  [
    "Full-Color listing",
    "Link to your website",
    "Fly-Inn Find game Participation",
    "Fly-Inn Plaque",
    "Instagram frame",
    "Blog just about YOU that links to your website",
    "First-Class Perks Pass",
  ],
  [
    "Full-Color listing",
    "Link to your website",
    "Fly-Inn Find game Participation",
    "Fly-Inn Plaque",
    "Instagram frame",
    "Blog just about YOU that links to your website",
    "First-Class Perks Pass",
    "$50 gift certificate plus another $50 gift certificate if you film your stay!",
    "15-second video in our social media accounts!",
  ],
  [
    "Full-Color listing",
    "Link to your website",
    "Fly-Inn Find game Participation",
    "Fly-Inn Plaque",
    "Instagram frame",
    "Blog just about YOU that links to your website",
    "First-Class Perks Pass",
    "$50 gift certificate plus another $50 gift certificate if you film your stay!",
    "30-second video in our social media accounts!",
    "Feature your business, FREE for 30 days",
    "Podcast/interview",
  ],
];

// PLAN_PRICING is still used for the initial display,
// but actual Stripe prices are fetched dynamically.
const PLAN_PRICING = [
  { yearly: 120, monthly: 15 },
  { yearly: 180, monthly: 27 },
  { yearly: 240, monthly: 36 },
];

const SelectPaymentPlan = ({
  selectedPlanIndex,
  setSelectedPlanIndex,
  plans,
  setCurrentStep,
  businessTypes, // New prop: array of strings, e.g., ['Aircraft Sales - New', 'Leasing & Fractional Ownership']
  selectedPlansAndBilling, // New prop: This will hold the consolidated data
  setSelectedPlansAndBilling, // New prop: function to update the consolidated data
}) => {
  // Refs for scrolling to billing options for each business type
  const billingRefs = useRef({});

  const handleSelectPlan = (businessType, planIdx) => {
    setSelectedPlanIndex((prev) => ({
      ...prev,
      [businessType]: planIdx,
    }));
    // Reset billing cycle for this business type when a new plan is selected
    setSelectedPlansAndBilling((prev) => ({
      ...prev,
      [businessType]: { freq: "", value: null, tiers: "" },
    }));

    setTimeout(() => {
      if (billingRefs.current[businessType]) {
        billingRefs.current[businessType].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  };

  const handleSelectBillingCycle = (businessType, freq, value, tierName) => {
    setSelectedPlansAndBilling((prev) => ({
      ...prev,
      [businessType]: { freq, value, tiers: tierName },
    }));
  };

  const renderBillingOptions = (businessType) => {
    const selectedIdx = selectedPlanIndex[businessType];
    const currentBillingCycle = selectedPlansAndBilling[businessType];

    if (selectedIdx === null || selectedIdx === undefined) {
      return (
        <div className="text-center text-gray-600 p-8 bg-gray-50 rounded-xl mt-6 animate-fadeIn">
          <CurrencyDollarIcon className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <p className="text-lg font-semibold">
            Select a plan above for "{getBusinessSubTypeLabel(businessType)}" to
            see billing options.
          </p>
          <p className="text-sm text-gray-500">
            Click on any of the pricing cards to choose your membership tier for
            this business type.
          </p>
        </div>
      );
    }

    const selectedTierName = tiers[selectedIdx];

    const monthlyPlan = plans.find(
      (plan) =>
        plan.name.toUpperCase().includes(selectedTierName) &&
        plan.name.toUpperCase().includes("MONTHLY")
    );

    const yearlyPlan = plans.find(
      (plan) =>
        plan.name.toUpperCase().includes(selectedTierName) &&
        plan.name.toUpperCase().includes("YEARLY")
    );

    if (!monthlyPlan || !yearlyPlan) {
      return (
        <div className="text-center text-red-700 p-8 bg-red-50 rounded-xl mt-6 animate-fadeIn">
          <p className="text-lg font-semibold">
            Billing details for {selectedTierName} for "{businessType}" not
            found. Please try again later.
          </p>
          <p className="text-sm text-red-600">
            There might be an issue fetching plan details from our payment
            provider.
          </p>
        </div>
      );
    }

    // Calculate savings for yearly plan
    const monthlyPriceYearly = monthlyPlan.price * 12;
    const yearlyPrice = yearlyPlan.price;
    const savings = monthlyPriceYearly - yearlyPrice;
    const savingsPercentage =
      monthlyPriceYearly > 0
        ? ((savings / monthlyPriceYearly) * 100).toFixed(0)
        : 0;

    return (
      <div className="mt-8 space-y-6 animate-fadeIn">
        <h3 className="text-2xl font-bold text-gray-800 text-center">
          Choose Billing Cycle for{" "}
          <span className="text-red-600">
            "{getBusinessSubTypeLabel(businessType)}"
          </span>{" "}
          ({selectedTierName} Plan)
        </h3>

        <div className="text-center text-gray-600 text-sm md:text-base">
          Select how you'd like to be billed for this plan.
        </div>

        <div className="flex flex-col md:flex-row justify-center gap-4">
          <button
            className={`px-6 border-solid py-3 rounded-md text-lg font-semibold border transition-all duration-150
            ${
              currentBillingCycle?.freq === "yearly"
                ? "bg-red-600 text-white border-red-600 ring-2 ring-red-200"
                : "bg-white text-red-700 border-red-300 hover:bg-red-50"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectBillingCycle(
                businessType,
                "yearly",
                yearlyPlan,
                selectedTierName
              );
            }}
          >
            Pay Yearly – ${yearlyPlan.price}/year
          </button>

          <button
            className={`px-6 py-3 border-solid rounded-md text-lg font-semibold border transition-all duration-150
            ${
              currentBillingCycle?.freq === "monthly"
                ? "bg-red-600 text-white border-red-600 ring-2 ring-red-200"
                : "bg-white text-red-700 border-red-300 hover:bg-red-50"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectBillingCycle(
                businessType,
                "monthly",
                monthlyPlan,
                selectedTierName
              );
            }}
          >
            Pay Monthly – ${monthlyPlan.price}/month
          </button>
        </div>

        {savings > 0 && (
          <p className="text-center text-sm text-green-700 mt-2">
            Save {savingsPercentage}% by choosing yearly billing (${savings}{" "}
            off)
          </p>
        )}

        {currentBillingCycle?.freq &&
          currentBillingCycle?.tiers === selectedTierName && (
            <div className="mt-6 text-center text-base font-medium text-gray-700">
              You selected the{" "}
              <span className="text-red-600 font-bold">{selectedTierName}</span>{" "}
              plan for{" "}
              <span className="text-red-600 font-bold">
                "{getBusinessSubTypeLabel(businessType)}"
              </span>
              , billed{" "}
              <span className="text-red-600 font-bold">
                {currentBillingCycle.freq}
              </span>{" "}
              at{" "}
              <span className="text-red-600 font-bold">
                ${currentBillingCycle.value.price}
                {currentBillingCycle.freq === "yearly" ? "/year" : "/month"}
              </span>
              .
            </div>
          )}
      </div>
    );
  };

  // Main loading state for the entire component
  if (plans?.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-red-500 mb-4"></div>
          <p className="text-lg text-gray-700">No plans found...</p>
        </div>
      </div>
    );
  }

  // Check if all business types have a selected plan and billing cycle
  const allPlansSelected = businessTypes.every((type) => {
    return (
      selectedPlanIndex[type] !== null &&
      selectedPlanIndex[type] !== undefined &&
      selectedPlansAndBilling[type]?.value?.product_id
    );
  });

  console.log({ plans, businessTypes, allPlansSelected });

  return (
    <div className="flex flex-col items-center w-full pb-8 !pt-0">
      {businessTypes.map((type, typeIdx) => (
        <div key={type} className="w-full mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-8 bg-gray-50 py-4 rounded-xl shadow-sm">
            Configure Plan for{" "}
            <span className="text-red-600">
              "{getBusinessSubTypeLabel(type)}"
            </span>
          </h2>
          <p className="text-center text-gray-600 mb-8 text-lg">
            Choose the perfect membership tier for your{" "}
            <span className="font-semibold text-red-700">
              "{getBusinessSubTypeLabel(type)}"
            </span>{" "}
            listing.
          </p>
          <div className="flex flex-col md:flex-row gap-6 w-full justify-center">
            {PLAN_PRICING.map((plan, planIdx) => (
              <div
                key={planIdx}
                className={`flex-1 border-solid hover:border-red-500 hover:border-solid hover:border rounded-2xl shadow-lg border-1 transition-all duration-200 bg-white px-4 py-5 flex flex-col items-center cursor-pointer relative
                  ${
                    selectedPlanIndex[type] === planIdx
                      ? "border-red-600 ring-2 ring-red-200 shadow-2xl z-10"
                      : "border-gray-200 hover:border-red-300"
                  }`}
                onClick={() => handleSelectPlan(type, planIdx)}
              >
                <div className="mb-5 w-full">
                  <h3 className="text-2xl font-bold text-red-700 mb-2 text-center">
                    {tiers[planIdx]} Plan
                  </h3>
                  <ul className="text-left space-y-2 !pl-0 mt-4">
                    {PLAN_FEATURES[planIdx].map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-gray-700"
                      >
                        <CheckCircleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div
                  className={`mt-auto mb-[10px] rounded-[10px] flex flex-col items-center py-3 rounded-b-xl w-[calc(100%+2px)] -mx-4 border-t border-gray-100 transition-all duration-200 cursor-pointer
                    ${
                      selectedPlanIndex[type] === planIdx
                        ? "bg-red-600 !text-white"
                        : "bg-red-50 hover:bg-red-100 text-red-700"
                    }`}
                >
                  <span
                    className={`text-3xl font-extrabold mb-1 ${
                      selectedPlanIndex[type] === planIdx ? "!text-white" : ""
                    }`}
                  >
                    ${plan.yearly}
                    <span
                      className={`text-lg font-normal ${
                        selectedPlanIndex[type] === planIdx
                          ? " !text-white"
                          : ""
                      }`}
                    >
                      /year
                    </span>
                  </span>
                  <span
                    className={`text-base font-medium ${
                      selectedPlanIndex[type] === planIdx ? "!text-white" : ""
                    }`}
                  >
                    or ${plan.monthly}/month
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div
            ref={(el) => (billingRefs.current[type] = el)}
            className="w-full mt-10"
          >
            {renderBillingOptions(type)}
          </div>
          {typeIdx < businessTypes.length - 1 && (
            <hr className="my-16 border-t-2 border-gray-200" />
          )}
        </div>
      ))}

      {/* Action Buttons */}
      <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-between w-full ">
        <button
          className="w-full sm:w-auto px-8 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-200 transition-colors duration-200 shadow-sm"
          onClick={() => setCurrentStep(1)}
        >
          Back
        </button>
        <button
          className={`w-full sm:w-auto px-8 py-3 font-medium rounded-lg transition-all duration-200 shadow-md
            ${
              allPlansSelected
                ? "bg-[#af2322] text-white hover:bg-[#9e1f1a] transform hover:scale-105"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          onClick={() => allPlansSelected && setCurrentStep(3)}
          disabled={!allPlansSelected}
        >
          Next: Payment
        </button>
      </div>
    </div>
  );
};

export default SelectPaymentPlan;
